/**
 * Runtime-generated textures.
 *
 * Every map is drawn on a 2D canvas at level load — the repo ships no binary
 * assets. Two constraints shape all of the code below:
 *
 *  - Maps must tile seamlessly. All noise is periodic (its lattice wraps at the
 *    tile edge) and anything drawn with canvas ops near an edge is drawn again
 *    on the opposite side.
 *  - Generation is deterministic. The stream is seeded from the level id so a
 *    level always looks the same, and no `Math.random` is used here.
 *
 * The generators are parameterised by `LevelSpec.palette` and switched on
 * `LevelSpec.generator`, so the lobby reads as damp wallpaper and carpet while
 * the poolrooms read as tile — from the same code paths.
 */

import * as THREE from 'three';
import { clamp01, hash2, smoothstep, TAU } from '../core/math';
import { hashSeed, makeRNG } from '../core/rng';
import type { LevelPalette, LevelSpec, RNG } from '../core/types';
import type { TextureSet } from './api';

/**
 * Canvas edge length. 512 is the sweet spot: a level's whole set generates in
 * around 100ms, which a level transition can absorb, and at the tile sizes
 * below it still gives 256-512 pixels per metre.
 */
const SIZE = 512;

/**
 * How many metres one tile of each map covers. `materials.ts` turns these into
 * `texture.repeat`, so the generators and the repeats cannot drift apart.
 */
export const TILE_METRES = {
  /** Two metres: wallpaper blotches repeating every metre reads as a pattern. */
  wall: 2,
  /** The trim map describes the bottom metre of a wall. */
  trim: 1,
  floor: 1,
  /** 2x2 panels of 0.6m, the standard suspended ceiling module. */
  ceiling: 1.2,
  light: 1,
  /** Caustics want to be broad and slow. */
  water: 4,
} as const;

/* ------------------------------------------------------------------ *
 * Canvas / colour helpers
 * ------------------------------------------------------------------ */

interface Surface {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

function surface(): Surface {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  // Everything here is written as ImageData and the floor is read back for its
  // normal map, so the software-backed path is the fast one.
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  return { canvas, ctx };
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

function rgb(hex: number): RGB {
  return { r: (hex >> 16) & 255, g: (hex >> 8) & 255, b: hex & 255 };
}

/** Multiply a colour's channels, staying in 0..255. */
function shade(c: RGB, mul: number): RGB {
  return { r: Math.min(255, c.r * mul), g: Math.min(255, c.g * mul), b: Math.min(255, c.b * mul) };
}

function mixRGB(a: RGB, b: RGB, t: number): RGB {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

/** Run `draw` nine times so anything it puts near an edge also lands on the far side. */
function wrapped(ctx: CanvasRenderingContext2D, draw: () => void): void {
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      ctx.save();
      ctx.translate(ox * SIZE, oy * SIZE);
      draw();
      ctx.restore();
    }
  }
}

/* ------------------------------------------------------------------ *
 * Tileable noise
 * ------------------------------------------------------------------ */

/**
 * Fractal value noise over the whole canvas, returned as `size * size` floats
 * in 0..1. The lattice is `cellsX` by `cellsY` and wraps, so the field tiles.
 *
 * Written as an incremental fill rather than a per-pixel `noise(x, y)` call:
 * the interpolation weights only depend on one axis, so they are computed once
 * per octave and the inner loop is four lookups and three lerps.
 */
function fbmField(cellsX: number, cellsY: number, octaves: number, seed: number, gain = 0.5): Float32Array {
  const out = new Float32Array(SIZE * SIZE);
  const ix0 = new Int32Array(SIZE);
  const ix1 = new Int32Array(SIZE);
  const fx = new Float32Array(SIZE);
  const iy0 = new Int32Array(SIZE);
  const iy1 = new Int32Array(SIZE);
  const fy = new Float32Array(SIZE);

  let norm = 0;
  for (let o = 0, a = 1; o < octaves; o++, a *= gain) norm += a;
  let amp = 1 / norm;

  let cx = cellsX;
  let cy = cellsY;
  for (let o = 0; o < octaves; o++) {
    const lattice = new Float32Array(cx * cy);
    for (let y = 0; y < cy; y++) {
      for (let x = 0; x < cx; x++) lattice[y * cx + x] = hash2(x, y, seed + o * 7919);
    }
    axisWeights(cx, ix0, ix1, fx);
    axisWeights(cy, iy0, iy1, fy);

    for (let y = 0; y < SIZE; y++) {
      const r0 = iy0[y]! * cx;
      const r1 = iy1[y]! * cx;
      const v = fy[y]!;
      const row = y * SIZE;
      for (let x = 0; x < SIZE; x++) {
        const a0 = ix0[x]!;
        const a1 = ix1[x]!;
        const u = fx[x]!;
        const top = lattice[r0 + a0]! + (lattice[r0 + a1]! - lattice[r0 + a0]!) * u;
        const bot = lattice[r1 + a0]! + (lattice[r1 + a1]! - lattice[r1 + a0]!) * u;
        out[row + x] += amp * (top + (bot - top) * v);
      }
    }
    amp *= gain;
    cx *= 2;
    cy *= 2;
  }
  return out;
}

/**
 * Per-pixel lattice indices and blend weight for one axis.
 *
 * Quintic rather than the usual cubic smoothstep: the floor's normal map is a
 * derivative of this field, and cubic interpolation leaves a discontinuity in
 * the second derivative that shows up as a visible grid of lattice lines once
 * you differentiate it.
 */
function axisWeights(cells: number, i0: Int32Array, i1: Int32Array, frac: Float32Array): void {
  const s = cells / SIZE;
  for (let i = 0; i < SIZE; i++) {
    const p = i * s;
    const c = Math.floor(p);
    const f = p - c;
    const a = ((c % cells) + cells) % cells;
    i0[i] = a;
    i1[i] = (a + 1) % cells;
    frac[i] = f * f * f * (f * (f * 6 - 15) + 10);
  }
}

/* ------------------------------------------------------------------ *
 * Wall surfaces
 * ------------------------------------------------------------------ */

/**
 * The famous mono-yellow. Cheap printed stripe, mottled value noise, drop
 * seams, damp blotches with a darker tide line at their edge, a few scuffs.
 *
 * The blotches are placed by a noise field rather than anchored to the bottom
 * of the tile: a bottom-anchored gradient would draw a hard damp line at every
 * tile boundary up the wall. The rising damp that sells the room lives in the
 * trim map instead, which really is anchored to the floor.
 */
function wallpaperCanvas(pal: LevelPalette, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const base = rgb(pal.wall);
  const stainCol = shade(mixRGB(base, rgb(pal.wallTrim), 0.55), 0.72);
  const mottle = fbmField(6, 6, 4, rng.int(0, 0xffff));
  const damp = fbmField(3, 3, 3, rng.int(0, 0xffff));
  const tooth = rng.int(0, 0xffff);

  // The pattern everyone actually pictures. Commercial vinyl wallcovering in
  // mustard, printed as a repeating stripe group rather than a plain wall: a
  // wide band, a dark pinstripe, a narrower band, another pinstripe. It has to
  // be clearly legible — a wall you can tell is *patterned* from across the
  // room is most of what makes the place read as the Backrooms and not as a
  // corridor painted beige.
  //
  // The group is 12.5cm so it divides the 2m tile a whole 16 times; anything
  // that does not divide evenly puts a hard seam down every tile boundary.
  const GROUP_PX = SIZE / 16;
  const dropPx = SIZE / 4; // 50cm paper drops
  /** Multiplier on the base tone at a given x, from the printed stripe group. */
  const stripeAt = (x: number): number => {
    const t = (x % GROUP_PX) / GROUP_PX;
    if (t < 0.545) return 1.045; // wide band, the lighter ground
    if (t < 0.605) return 0.9; // pinstripe
    if (t < 0.94) return 0.988; // narrow band
    return 0.912; // second pinstripe, a shade softer
  };

  for (let y = 0; y < SIZE; y++) {
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      let v = (1 + (mottle[idx]! - 0.5) * 0.15) * stripeAt(x);
      // The seam between paper drops is the only hard vertical line.
      const seam = Math.abs(((x + dropPx * 0.5) % dropPx) - dropPx * 0.5);
      if (seam < 1.6) v *= 0.962;
      v *= 0.986 + hash2(x, y, tooth) * 0.028;

      // Damp is a minority of the wall: a few patches with a darker tide line
      // where each one dried out. Any more and it reads as camouflage.
      const d = damp[idx]!;
      const patch = smoothstep(0.63, 0.8, d) * 0.42;
      const tide = Math.exp(-Math.abs(d - 0.63) * 44) * 0.2;
      const wet = clamp01(patch + tide);

      const c = mixRGB(shade(base, v), stainCol, wet);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  ctx.lineCap = 'round';
  for (let i = 0; i < 16; i++) {
    const x = rng.range(0, SIZE);
    const y = rng.range(0, SIZE);
    const ang = rng.chance(0.7) ? rng.range(-0.3, 0.3) : Math.PI / 2 + rng.range(-0.3, 0.3);
    const len = rng.range(5, 44);
    ctx.strokeStyle = rng.chance(0.45) ? 'rgba(255,252,225,0.06)' : 'rgba(52,44,18,0.075)';
    ctx.lineWidth = rng.range(0.6, 2.1);
    wrapped(ctx, () => {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
      ctx.stroke();
    });
  }
  return canvas;
}

/** Poured concrete: broad mottling, aggregate pitting, form-board joins, hairline cracks. */
/**
 * Painted weatherboard: the outside of a house on Level 94.
 *
 * The default wall is vinyl wallcovering with damp blotches in it, which is
 * correct for every level in the building and exactly wrong for the one that is
 * a model village. Two things go wrong when the Lobby's paper is stretched over
 * a suburb. The register: Level 94 is the one place in the game with no grime on
 * it, and that cleanness is what makes it wrong, so a stained house is the
 * level's premise coming apart. And the scale: indoors nobody ever sees more
 * than three metres of one wall, but out here a row of houses is forty metres of
 * unbroken elevation, and the same blotch repeats twenty times across it.
 *
 * So: horizontal boards with a shadow under each lap, an even coat of paint, and
 * nothing else. No stains, no seams, no wear — the whole texture is a value
 * gradient of about 8%, which at this distance reads as *painted* rather than as
 * flat white. The board pitch divides the tile so nothing lands on a seam.
 */
function sidingCanvas(pal: LevelPalette, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const base = rgb(pal.wall);
  // 8 boards to the 2 m tile: 25 cm to the course, which is clapboard.
  const BOARD = SIZE / 8;
  const grain = fbmField(2, 14, 3, rng.int(0, 0xffff));
  const tooth = rng.int(0, 0xffff);

  for (let y = 0; y < SIZE; y++) {
    const t = (y % BOARD) / BOARD;
    // Each board is proud at its bottom edge and shaded where the next one laps
    // over it. That one shadow line is the whole read.
    let board = 1.03 - t * 0.06;
    if (t < 0.09) board *= 0.9;
    else if (t > 0.93) board *= 1.035;
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      const v = board * (1 + (grain[idx]! - 0.5) * 0.03) * (0.994 + hash2(x, y, tooth) * 0.012);
      const c = shade(base, v);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/**
 * Mown grass: the lawn on Level 94.
 *
 * The carpet generator gives loop pile with wear and stains trodden into it,
 * which is the right ground for a building and the wrong ground for a lawn — and
 * the stains are the same problem as the wallpaper's. Grass instead: fine blade
 * noise, and the broad bands a mower leaves, which are the only thing that says
 * somebody cut this and the only large-scale feature a lawn has.
 */
function lawnCanvas(pal: LevelPalette, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const base = rgb(pal.carpet);
  const deep = rgb(pal.carpetStain);
  const clump = fbmField(9, 9, 3, rng.int(0, 0xffff));
  // The nap, laid one way. Hard alternating stripes were the obvious way to draw
  // a mown lawn and the wrong one: the floor tile is a metre, so a stripe that
  // alternates within it is 25 cm wide, and a whole level of 25 cm bands is
  // corduroy. A stretched noise field gives the same directional grain with no
  // period to lock onto.
  const nap = fbmField(2, 7, 2, rng.int(0, 0xffff));
  const blade = rng.int(0, 0xffff);

  for (let y = 0; y < SIZE; y++) {
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      const h = hash2(x, y, blade);
      const v = (1 + (nap[idx]! - 0.5) * 0.07) * (1 + (clump[idx]! - 0.5) * 0.16) * (0.93 + h * 0.14);
      let c = shade(base, v);
      // The darker green of the thicker patches, not a stain.
      c = mixRGB(c, deep, smoothstep(0.62, 0.95, clump[idx]!) * 0.3);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function concreteCanvas(baseHex: number, darkHex: number, rng: RNG, cracks: number): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const base = rgb(baseHex);
  const dark = rgb(darkHex);
  const broad = fbmField(3, 3, 3, rng.int(0, 0xffff));
  const fine = fbmField(12, 12, 3, rng.int(0, 0xffff));
  const grime = fbmField(2, 2, 2, rng.int(0, 0xffff));
  const pit = rng.int(0, 0xffff);

  for (let y = 0; y < SIZE; y++) {
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      let v = 1 + (broad[idx]! - 0.5) * 0.2 + (fine[idx]! - 0.5) * 0.12;
      const h = hash2(x, y, pit);
      v *= 0.962 + h * 0.076;
      // Sparse dark aggregate and blown air pockets.
      if (h > 0.9955) v *= 0.66;
      let c = shade(base, v);
      c = mixRGB(c, dark, smoothstep(0.56, 0.9, grime[idx]!) * 0.35);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  // Form-board joins: one horizontal and one vertical control joint per tile.
  ctx.strokeStyle = 'rgba(0,0,0,0.16)';
  ctx.lineWidth = 1.4;
  const jx = Math.floor(rng.range(0, SIZE));
  const jy = Math.floor(rng.range(0, SIZE));
  ctx.beginPath();
  ctx.moveTo(jx + 0.5, 0);
  ctx.lineTo(jx + 0.5, SIZE);
  ctx.moveTo(0, jy + 0.5);
  ctx.lineTo(SIZE, jy + 0.5);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(0,0,0,0.2)';
  for (let i = 0; i < cracks; i++) {
    let x = rng.range(0, SIZE);
    let y = rng.range(0, SIZE);
    let ang = rng.range(0, TAU);
    const pts: number[] = [x, y];
    const steps = rng.int(4, 9);
    for (let s = 0; s < steps; s++) {
      ang += rng.range(-0.7, 0.7);
      x += Math.cos(ang) * rng.range(6, 22);
      y += Math.sin(ang) * rng.range(6, 22);
      pts.push(x, y);
    }
    ctx.lineWidth = rng.range(0.5, 1.2);
    wrapped(ctx, () => {
      ctx.beginPath();
      ctx.moveTo(pts[0]!, pts[1]!);
      for (let p = 2; p < pts.length; p += 2) ctx.lineTo(pts[p]!, pts[p + 1]!);
      ctx.stroke();
    });
  }
  return canvas;
}

/**
 * Common brick in stretcher bond. Level 3's own surface, and the only brick in
 * the game.
 *
 * The wall tile is 2 m across 512 px, so the module is chosen to divide it
 * exactly and still be a real brick: 26 courses of 77 mm and 9 bricks of 222 mm
 * per tile, against a nominal 75 mm course and a 225 mm brick. 26 is even, so
 * the half-brick offset on alternate courses has the same parity at the top of
 * the tile as at the bottom and the bond survives the vertical wrap.
 *
 * Each brick gets its own tone off a hash of its course and index — a brick
 * wall is not one colour, and a wall of identical bricks reads as printed
 * paper, which is exactly what this replaced.
 */
function brickCanvas(baseHex: number, darkHex: number, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const COURSES = 26;
  const PER_COURSE = 9;
  const courseH = SIZE / COURSES;
  const brickW = SIZE / PER_COURSE;

  const base = rgb(baseHex);
  const dark = rgb(darkHex);
  // Mortar is never the brick's colour. Pull the palette's dark swatch most of
  // the way to a dusty grey so the joints read as a different material.
  const mortar = mixRGB(dark, { r: 152, g: 148, b: 140 }, 0.72);
  const grime = fbmField(2, 2, 3, rng.int(0, 0xffff));
  const face = fbmField(16, 16, 3, rng.int(0, 0xffff));
  const tone = rng.int(0, 0xffff);
  const speck = rng.int(0, 0xffff);
  // Half the joint width in pixels. 10 mm of a 77 mm course.
  const joint = courseH * 0.13;

  for (let y = 0; y < SIZE; y++) {
    const row = y * SIZE;
    const cy = Math.floor(y / courseH);
    const fy = y / courseH - cy;
    // Stretcher bond: every other course starts half a brick along.
    const shift = (cy & 1) === 0 ? 0 : brickW * 0.5;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      const bx = (x + shift) / brickW;
      const cx = Math.floor(bx);
      const fx = bx - cx;

      // Pixels to the nearest joint, horizontally and vertically.
      const dv = Math.min(fy, 1 - fy) * courseH;
      const dh = Math.min(fx, 1 - fx) * brickW;
      const m = 1 - smoothstep(joint * 0.6, joint * 1.9, Math.min(dv, dh));

      // Per-brick tone. Wide, because clay is fired in batches.
      const h = hash2(cx, cy, tone);
      let v = 0.8 + h * 0.42;
      // Face texture, and the odd blown or spalled brick.
      v *= 0.94 + face[idx]! * 0.12;
      if (h > 0.965) v *= 0.72;
      // The brick's own edges catch a little more light than its middle.
      v += (1 - smoothstep(joint * 1.4, joint * 5, Math.min(dv, dh))) * 0.05;
      // Sand speckle in the clay.
      v *= 0.98 + hash2(x, y, speck) * 0.04;

      let c = shade(base, v);
      c = mixRGB(c, shade(mortar, 0.94 + face[idx]! * 0.14), m);
      // Soot and damp, in broad drifts across the whole wall.
      c = shade(c, 1 - smoothstep(0.5, 0.95, grime[idx]!) * 0.28);

      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  // Efflorescence: salt drawn out of the wall and left as a pale bloom. The one
  // thing that tells you the brick has something wet behind it.
  for (let i = 0; i < 3; i++) {
    const x = rng.range(0, SIZE);
    const y = rng.range(0, SIZE);
    const r = rng.range(28, 72);
    wrapped(ctx, () => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(226,222,210,${rng.range(0.1, 0.2).toFixed(3)})`);
      g.addColorStop(1, 'rgba(226,222,210,0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    });
  }
  return canvas;
}

/** Grimy painted metal: panel seams, rivets, vertical rust streaks, scratches. */
function metalCanvas(baseHex: number, darkHex: number, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const base = rgb(baseHex);
  const dark = rgb(darkHex);
  const rust = { r: 96, g: 54, b: 28 };
  // Few cells across, many down: the field stretches into vertical weep streaks.
  const streak = fbmField(24, 3, 3, rng.int(0, 0xffff));
  const wear = fbmField(4, 4, 3, rng.int(0, 0xffff));
  const grain = rng.int(0, 0xffff);

  for (let y = 0; y < SIZE; y++) {
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      let v = 1 + (wear[idx]! - 0.5) * 0.16;
      // Brushed sheen along the panel.
      v *= 0.97 + hash2(x, y >> 2, grain) * 0.06;
      let c = shade(base, v);
      c = mixRGB(c, dark, smoothstep(0.5, 0.85, wear[idx]!) * 0.4);
      c = mixRGB(c, rust, smoothstep(0.62, 0.9, streak[idx]!) * 0.5);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  // Two horizontal panel seams with rivets along them.
  for (const sy of [SIZE * 0.25, SIZE * 0.75]) {
    ctx.strokeStyle = 'rgba(0,0,0,0.32)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, sy);
    ctx.lineTo(SIZE, sy);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, sy + 1.5);
    ctx.lineTo(SIZE, sy + 1.5);
    ctx.stroke();
    for (let i = 0; i < 16; i++) {
      const rx = (i + 0.5) * (SIZE / 16);
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.beginPath();
      ctx.arc(rx, sy - 0.5, 2.4, 0, TAU);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.beginPath();
      ctx.arc(rx - 0.7, sy - 1.3, 1.2, 0, TAU);
      ctx.fill();
    }
  }
  return canvas;
}

/** Glossy square tile with grout lines, a bevel and a diagonal sheen. */
function tileCanvas(baseHex: number, groutHex: number, rng: RNG, tiles: number): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  // Glazed tile is never as bright as the palette swatch: leaving headroom
  // stops a lit wall clipping to flat white and swallowing the grout.
  const base = shade(rgb(baseHex), 0.93);
  const grout = shade(rgb(groutHex), 0.62);
  const dirt = fbmField(4, 4, 3, rng.int(0, 0xffff));
  const seed = rng.int(0, 0xffff);
  const cell = SIZE / tiles;

  for (let y = 0; y < SIZE; y++) {
    const ty = y / cell;
    const cy = Math.floor(ty);
    const fy = ty - cy;
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      const tx = x / cell;
      const cx = Math.floor(tx);
      const fx = tx - cx;

      // Distance in pixels to the nearest grout line.
      const d = Math.min(fx, 1 - fx, fy, 1 - fy) * cell;
      const g = 1 - smoothstep(1.2, 3.0, d);
      // Bevelled edge: catches light top-left, shades bottom-right.
      const bevel = (1 - smoothstep(2.5, 7.0, d)) * (fx + (1 - fy) - 1) * 0.5;

      let v = 1 + (hash2(cx, cy, seed) - 0.5) * 0.055 + bevel * 0.22;
      v += (0.5 - fy) * 0.03 + (fx - 0.5) * 0.015; // faint gloss gradient per tile
      let c = shade(base, v);
      c = mixRGB(c, grout, g);
      // Grime settles in the grout and in the corners.
      c = shade(c, 1 - smoothstep(0.55, 0.95, dirt[idx]!) * 0.14 * (0.3 + g));

      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  // A couple of chipped or discoloured tiles keep the grid from reading as printed.
  for (let i = 0; i < 3; i++) {
    const cx = rng.int(0, tiles - 1);
    const cy = rng.int(0, tiles - 1);
    ctx.fillStyle = `rgba(0,0,0,${rng.range(0.03, 0.08).toFixed(3)})`;
    ctx.fillRect(cx * cell + 1, cy * cell + 1, cell - 2, cell - 2);
  }
  return canvas;
}

/* ------------------------------------------------------------------ *
 * Floors
 * ------------------------------------------------------------------ */

/** Short commercial loop pile: high-frequency speckle, row grain, wear, stains. */
function carpetCanvas(pal: LevelPalette, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const base = rgb(pal.carpet);
  const stain = rgb(pal.carpetStain);
  const wear = fbmField(4, 4, 3, rng.int(0, 0xffff));
  const patch = fbmField(3, 3, 3, rng.int(0, 0xffff));
  const s1 = rng.int(0, 0xffff);
  const s2 = rng.int(0, 0xffff);
  const rowStep = (128 * TAU) / SIZE; // 128 loop rows per metre

  for (let y = 0; y < SIZE; y++) {
    const rowLine = Math.sin(y * rowStep) * 0.5 + 0.5;
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      // Per-pixel tuft plus a vertically elongated tuft so the pile has a lay.
      const speck = hash2(x, y, s1) * 0.6 + hash2(x, y >> 2, s2) * 0.4;
      let v = 0.84 + speck * 0.3 + (wear[idx]! - 0.5) * 0.16 + (rowLine - 0.5) * 0.04;
      let c = shade(base, v);
      c = mixRGB(c, stain, smoothstep(0.7, 0.86, patch[idx]!) * 0.55);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/* ------------------------------------------------------------------ *
 * Ceiling
 * ------------------------------------------------------------------ */

/**
 * Suspended acoustic tiles: a T-bar grid, the characteristic fine random
 * perforations, per-panel value variation, and the odd brown water stain.
 */
function acousticCanvas(pal: LevelPalette, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const base = rgb(pal.ceiling);
  const stain = shade(mixRGB(base, { r: 120, g: 86, b: 44 }, 0.6), 0.9);
  const panels = 2;
  const cell = SIZE / panels;
  const mottle = fbmField(8, 8, 3, rng.int(0, 0xffff));
  const leak = fbmField(2, 2, 3, rng.int(0, 0xffff));
  const panelSeed = rng.int(0, 0xffff);
  const dots = rng.int(0, 0xffff);
  const jitterA = dots + 17;
  const jitterB = dots + 91;

  for (let y = 0; y < SIZE; y++) {
    const cy = Math.floor(y / cell);
    const fy = y / cell - cy;
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      const cx = Math.floor(x / cell);
      const fx = x / cell - cx;

      const d = Math.min(fx, 1 - fx, fy, 1 - fy) * cell;
      // The grid is a dark groove with a bright aluminium T-bar sitting in it.
      const groove = 1 - smoothstep(1.0, 2.6, d);
      const bar = (1 - smoothstep(0.2, 1.2, Math.abs(d - 2.6))) * 0.5;

      let v = 1 + (hash2(cx, cy, panelSeed) - 0.5) * 0.07 + (mottle[idx]! - 0.5) * 0.08;

      // Perforations: one jittered hole per 4px cell, 62% of cells filled.
      const px4 = x >> 2;
      const py4 = y >> 2;
      if (hash2(px4, py4, dots) > 0.38) {
        const hx = (px4 << 2) + 1 + hash2(px4, py4, jitterA) * 2;
        const hy = (py4 << 2) + 1 + hash2(px4, py4, jitterB) * 2;
        const dd = Math.hypot(x - hx, y - hy);
        v *= 1 - 0.3 * (1 - smoothstep(0.4, 1.5, dd));
      }

      v *= 1 - groove * 0.42;
      v += bar * 0.1;
      let c = shade(base, v);
      // Water damage bleeds through the odd region of the grid.
      c = mixRGB(c, stain, smoothstep(0.72, 0.9, leak[idx]!) * 0.55);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/* ------------------------------------------------------------------ *
 * Trim, light panel, water
 * ------------------------------------------------------------------ */

/**
 * The bottom metre of a wall: skirting at the floor with rising damp above it.
 *
 * Canvas row 0 is the top because three flips canvas textures on upload, so
 * v = 0 is the last row — the floor. Materials clamp V, which means a shorter
 * trim band simply shows the bottom of this design.
 */
function trimCanvas(spec: LevelSpec, rng: RNG): HTMLCanvasElement {
  const pal = spec.palette;
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const tiled = spec.generator === 'poolrooms';
  const wall = rgb(pal.wall);
  const board = shade(rgb(pal.wallTrim), tiled ? 0.86 : 0.78);
  const damp = shade(mixRGB(rgb(pal.wall), rgb(pal.carpetStain), 0.6), 0.8);
  // Level 94's houses have a painted plinth and nothing else. Rising damp round
  // the bottom of every one of them is the same mistake as stained wallpaper on
  // the outside of them: the level's whole register is that nothing here has
  // ever been weathered.
  const rising = spec.generator === 'motion' ? 0 : 1;

  const boardTop = SIZE * (tiled ? 0.24 : 0.13); // height of the skirting, from the floor
  const mottle = fbmField(6, 6, 3, rng.int(0, 0xffff));
  const edge = fbmField(9, 2, 3, rng.int(0, 0xffff));
  const tooth = rng.int(0, 0xffff);
  const stripeStep = (13 * TAU) / SIZE; // matches the wallpaper's stripe pitch

  for (let y = 0; y < SIZE; y++) {
    const up = SIZE - 1 - y; // pixels above the floor
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      let c: RGB;
      if (up < boardTop) {
        // Skirting board: darker, a highlight along its top lip, scuffed.
        let v = 1 + (mottle[idx]! - 0.5) * 0.1;
        if (boardTop - up < 2.5) v *= 1.16;
        if (up < 2) v *= 0.7;
        v *= 0.99 + hash2(x, y, tooth) * 0.02;
        c = shade(board, v);
      } else {
        const h = (up - boardTop) / (SIZE - boardTop);
        let v = 1 + (mottle[idx]! - 0.5) * 0.12 + Math.sin(x * stripeStep) * 0.02;
        v *= 0.988 + hash2(x, y, tooth) * 0.024;
        c = shade(wall, v);
        // Rising damp: strongest at the skirting, ragged top edge, with a
        // concentrated tide mark where it gives out.
        const reach = 0.3 + (edge[idx]! - 0.5) * 0.26;
        const wet = clamp01(1 - h / Math.max(reach, 0.05));
        const tide = Math.exp(-Math.abs(h - reach) * 20) * 0.22;
        c = mixRGB(c, damp, clamp01(wet * wet * 0.5 + tide) * rising);
      }
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  if (tiled) {
    // Poolrooms get a darker tile course instead of a board: draw the grout.
    ctx.strokeStyle = 'rgba(0,0,0,0.18)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 8; i++) {
      const gx = i * (SIZE / 8);
      ctx.moveTo(gx, SIZE - boardTop);
      ctx.lineTo(gx, SIZE);
    }
    ctx.moveTo(0, SIZE - boardTop);
    ctx.lineTo(SIZE, SIZE - boardTop);
    ctx.stroke();
  }
  return canvas;
}

/**
 * A diffusing fluorescent panel: bright core, three visible tubes behind the
 * diffuser, dimmer towards the frame. Tiles as a run of fixtures.
 */
function lightPanelCanvas(pal: LevelPalette, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const tint = rgb(pal.lightTint);
  const frame = shade(rgb(pal.wallTrim), 0.55);
  const dust = fbmField(5, 5, 3, rng.int(0, 0xffff));
  const tubes = [0.22, 0.5, 0.78];

  for (let y = 0; y < SIZE; y++) {
    const v = y / SIZE;
    const row = y * SIZE;
    let tube = 0;
    for (const t of tubes) {
      const d = (v - t) / 0.055;
      tube += Math.exp(-d * d);
    }
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      const u = x / SIZE;
      // Distance to the nearest panel edge, in 0..0.5.
      const border = Math.min(u, 1 - u, v, 1 - v);
      const inner = smoothstep(0.0, 0.075, border);
      const core = 0.34 + 0.36 * smoothstep(0.03, 0.3, border);
      let b = (core + tube * 0.3) * inner;
      b *= 1 - dust[idx]! * 0.1; // dust and dead flies on the diffuser
      b = clamp01(b);
      const c = shade(mixRGB(frame, tint, b), 0.5 + b * 0.55);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/**
 * Shallow-water caustics. Three plane waves with integer frequencies (so the
 * pattern tiles) warped by a tileable noise field; the web forms where they
 * cancel. Alpha carries the surface, so the material can be transparent.
 */
function waterCanvas(pal: LevelPalette, rng: RNG): HTMLCanvasElement {
  const { canvas, ctx } = surface();
  const img = ctx.createImageData(SIZE, SIZE);
  const px = img.data;

  const deep = rgb(pal.carpet);
  const bright = { r: 255, g: 255, b: 255 };
  const warpA = fbmField(3, 3, 3, rng.int(0, 0xffff));
  const warpB = fbmField(4, 4, 2, rng.int(0, 0xffff));
  const waves: Array<[number, number]> = [
    [2, 3],
    [-3, 2],
    [4, -1],
  ];

  for (let y = 0; y < SIZE; y++) {
    const v = y / SIZE;
    const row = y * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const idx = row + x;
      const u = x / SIZE;
      const wa = (warpA[idx]! - 0.5) * 2.4;
      const wb = (warpB[idx]! - 0.5) * 1.4;
      let s = 0;
      for (const [fx, fy] of waves) s += Math.sin((fx * u + fy * v) * TAU + wa + wb);
      const caustic = Math.pow(clamp01(1 - Math.abs(s) * 0.52), 4);
      const c = mixRGB(shade(deep, 0.92 + wa * 0.04), bright, caustic * 0.85);
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255 * (0.34 + caustic * 0.4);
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/* ------------------------------------------------------------------ *
 * Normal map
 * ------------------------------------------------------------------ */

/**
 * Derive a tangent-space normal map from a colour map's luminance.
 *
 * The floor is the surface grazing fluorescent light actually rakes across, so
 * this is what makes carpet read as carpet rather than as a flat colour. The
 * height field is box-blurred first: sobel straight off per-pixel carpet
 * speckle produces a normal map that fizzes under any camera motion.
 */
function normalFromLuminance(src: HTMLCanvasElement, strength: number): HTMLCanvasElement {
  const sctx = src.getContext('2d', { willReadFrequently: true })!;
  const data = sctx.getImageData(0, 0, SIZE, SIZE).data;

  const height = new Float32Array(SIZE * SIZE);
  for (let i = 0, p = 0; i < height.length; i++, p += 4) {
    height[i] = (data[p]! * 0.299 + data[p + 1]! * 0.587 + data[p + 2]! * 0.114) / 255;
  }
  const blur = new Float32Array(SIZE * SIZE);
  for (let y = 0; y < SIZE; y++) {
    const ym = ((y - 1 + SIZE) % SIZE) * SIZE;
    const y0 = y * SIZE;
    const yp = ((y + 1) % SIZE) * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const xm = (x - 1 + SIZE) % SIZE;
      const xp = (x + 1) % SIZE;
      blur[y0 + x] =
        (height[ym + xm]! +
          height[ym + x]! +
          height[ym + xp]! +
          height[y0 + xm]! +
          height[y0 + x]! +
          height[y0 + xp]! +
          height[yp + xm]! +
          height[yp + x]! +
          height[yp + xp]!) /
        9;
    }
  }

  const { canvas, ctx } = surface();
  const out = ctx.createImageData(SIZE, SIZE);
  const px = out.data;
  for (let y = 0; y < SIZE; y++) {
    const ym = ((y - 1 + SIZE) % SIZE) * SIZE;
    const y0 = y * SIZE;
    const yp = ((y + 1) % SIZE) * SIZE;
    for (let x = 0; x < SIZE; x++) {
      const xm = (x - 1 + SIZE) % SIZE;
      const xp = (x + 1) % SIZE;
      const dx =
        blur[ym + xp]! + 2 * blur[y0 + xp]! + blur[yp + xp]! - (blur[ym + xm]! + 2 * blur[y0 + xm]! + blur[yp + xm]!);
      const dy =
        blur[yp + xm]! + 2 * blur[yp + x]! + blur[yp + xp]! - (blur[ym + xm]! + 2 * blur[ym + x]! + blur[ym + xp]!);
      // n = (-dh/du, -dh/dv, 1). v runs opposite to canvas rows once three
      // flips the image, so the y gradient keeps its sign and x flips.
      const nx = -dx * strength;
      const ny = dy * strength;
      const inv = 1 / Math.hypot(nx, ny, 1);
      const i = (y0 + x) * 4;
      px[i] = (nx * inv * 0.5 + 0.5) * 255;
      px[i + 1] = (ny * inv * 0.5 + 0.5) * 255;
      px[i + 2] = (inv * 0.5 + 0.5) * 255;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(out, 0, 0);
  return canvas;
}

/* ------------------------------------------------------------------ *
 * Assembly
 * ------------------------------------------------------------------ */

function toTexture(canvas: HTMLCanvasElement, colorSpace: THREE.ColorSpace, clampV = false): THREE.Texture {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = clampV ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
  t.colorSpace = colorSpace;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  // Three clamps this to the hardware maximum when it uploads the texture, so
  // asking for 16 means "as much as this GPU has".
  t.anisotropy = 16;
  t.needsUpdate = true;
  return t;
}

function wallCanvasFor(spec: LevelSpec, rng: RNG): HTMLCanvasElement {
  const pal = spec.palette;
  switch (spec.generator) {
    case 'warehouse':
    // Bare concrete, which is all the wiki says Level 6 is made of.
    case 'lightsout':
      return concreteCanvas(pal.wall, pal.wallTrim, rng, 3);
    // Concrete facades, weathered harder than an interior wall ever gets.
    case 'city':
      return concreteCanvas(pal.wall, pal.wallTrim, rng, 6);
    case 'pipes':
      return metalCanvas(pal.wall, pal.wallTrim, rng);
    // Level 3's one visibly wrong surface until now: it was borrowing the pipe
    // level's panelled metal, and the wiki is explicit that this level is
    // brick. It is the only brick in the game.
    case 'station':
      return brickCanvas(pal.wall, pal.wallTrim, rng);
    case 'poolrooms':
      return tileCanvas(pal.wall, pal.wallTrim, rng, 16);
    case 'motion':
      return sidingCanvas(pal, rng);
    // Painted plasterboard and hotel wallpaper are both this.
    case 'offices':
    case 'hotel':
    default:
      return wallpaperCanvas(pal, rng);
  }
}

function floorCanvasFor(spec: LevelSpec, rng: RNG): HTMLCanvasElement {
  const pal = spec.palette;
  switch (spec.generator) {
    case 'warehouse':
    case 'lightsout':
      return concreteCanvas(pal.carpet, pal.carpetStain, rng, 5);
    case 'pipes':
    case 'station':
      return concreteCanvas(pal.carpet, pal.carpetStain, rng, 7);
    // Asphalt: the most cracked surface in the game, and nothing has repaired
    // it in however long the level has been running.
    case 'city':
      return concreteCanvas(pal.carpet, pal.carpetStain, rng, 11);
    case 'poolrooms':
      return tileCanvas(pal.carpet, pal.carpetStain, rng, 8);
    case 'motion':
      return lawnCanvas(pal, rng);
    case 'offices':
    case 'hotel':
    default:
      return carpetCanvas(pal, rng);
  }
}

function ceilingCanvasFor(spec: LevelSpec, rng: RNG): HTMLCanvasElement {
  const pal = spec.palette;
  switch (spec.generator) {
    case 'warehouse':
    case 'lightsout':
    // Level 11's streets have no ceiling; where the renderer needs one it is
    // the underside of something concrete.
    case 'city':
      return concreteCanvas(pal.ceiling, pal.wallTrim, rng, 2);
    case 'pipes':
    case 'station':
      return metalCanvas(pal.ceiling, pal.carpetStain, rng);
    case 'poolrooms':
      return tileCanvas(pal.ceiling, pal.wallTrim, rng, 8);
    // A suspended ceiling is the office's, and the hotel's is plastered — both
    // read close enough at the tile scale, and the office's grid is the point.
    case 'offices':
    case 'hotel':
    default:
      return acousticCanvas(pal, rng);
  }
}

/** How hard the floor normal map pushes. Carpet is the whole point; tile is nearly flat. */
function floorNormalStrength(spec: LevelSpec): number {
  switch (spec.generator) {
    case 'poolrooms':
      return 1.1;
    case 'warehouse':
    case 'pipes':
    case 'station':
    case 'lightsout':
      return 2.4;
    // Asphalt at concrete strength turns every crack into a trip hazard you can
    // see from across the street.
    case 'city':
      return 1.9;
    // Grass, and the only large feature in it is the mower's stripes. At carpet
    // strength those come out as a ridge every metre across the whole level.
    case 'motion':
      return 1.5;
    default:
      return 3.4;
  }
}

/**
 * Build every map for one level. Deterministic: the stream is seeded from the
 * level id, so the same level always looks the same.
 */
export function makeTextureSet(spec: LevelSpec): TextureSet {
  const rng = makeRNG(hashSeed(spec.id));
  const made: THREE.Texture[] = [];
  const track = (t: THREE.Texture): THREE.Texture => {
    made.push(t);
    return t;
  };

  const floorCanvas = floorCanvasFor(spec, rng);

  const wall = track(toTexture(wallCanvasFor(spec, rng), THREE.SRGBColorSpace));
  const wallTrim = track(toTexture(trimCanvas(spec, rng), THREE.SRGBColorSpace, true));
  const floor = track(toTexture(floorCanvas, THREE.SRGBColorSpace));
  const floorNormal = track(
    toTexture(normalFromLuminance(floorCanvas, floorNormalStrength(spec)), THREE.NoColorSpace)
  );
  const ceiling = track(toTexture(ceilingCanvasFor(spec, rng), THREE.SRGBColorSpace));
  const lightPanel = track(toTexture(lightPanelCanvas(spec.palette, rng), THREE.SRGBColorSpace));
  const water = spec.waterDepth ? track(toTexture(waterCanvas(spec.palette, rng), THREE.SRGBColorSpace)) : null;

  return {
    wall,
    wallTrim,
    floor,
    floorNormal,
    ceiling,
    lightPanel,
    water,
    dispose() {
      for (const t of made) t.dispose();
      made.length = 0;
    },
  };
}
